/* FREUDEBOX: central sticker comparison. No canvas, tracking or storage. */
(() => {
  'use strict';
  if (window.FreudeboxRubbel) return;
  const states = new Map();
  const subscribers = new Map();
  const revealedByCustomer = new Set();
  const animations = new Map();
  function cancelAnimation(key) {
    const animation = animations.get(key);
    if (animation) cancelAnimationFrame(animation.frame);
    animations.delete(key);
  }
  function publish(key, value, animate = false) {
    states.set(key, value);
    for (const element of subscribers.get(key) || []) {
      element.dataset.animate = String(animate);
      element.render(value);
    }
  }
  const clamp = value => {
    const number = Number(value);
    return Number.isFinite(number) ? Math.max(0, Math.min(100, number)) : 0;
  };
  class RubbelCompare extends HTMLElement {
    connectedCallback() {
      if (this._active) return;
      this._frame = requestAnimationFrame(() => this.init());
    }
    init() {
      if (!this.isConnected || this._active) return;
      this._range = this.querySelector('.fb-rubbel__range');
      this._image = this.querySelector('.fb-rubbel__sticker');
      if (!this._range || !this._image) return;
      this._active = true;
      this._key = this.dataset.key;
      this._abort = new AbortController();
      const signal = this._abort.signal;
      if (!subscribers.has(this._key)) subscribers.set(this._key, new Set());
      subscribers.get(this._key).add(this);
      if (!states.has(this._key)) states.set(this._key, clamp(this.dataset.start));
      // Use native dragging, with continuous values and a track aligned to the visible grip.
      this._range.step = "any";
      // Stop only events
      // originating in our controls, before Flickity/PhotoSwipe can consume them.
      for (const eventName of ['pointerdown','pointermove','pointerup','pointercancel','mousedown','mousemove','mouseup','touchstart','touchmove','touchend','keydown','keyup']) {
        this.addEventListener(eventName, event => {
          if (event.target.closest('input,button')) {
            if (['pointerdown','mousedown','touchstart','keydown'].includes(eventName)) {
              cancelAnimation(this._key);
              revealedByCustomer.add(this._key);
            }
            event.stopPropagation();
          }
        }, { signal, passive: true });
      }
      this._range.addEventListener('input', event => {
        event.stopPropagation();
        this.setValue(event.target.value, false);
      }, { signal });
      this.addEventListener('click', event => {
        if (!event.target.closest('input,button')) return;
        event.stopPropagation();
        if (event.target.closest('button')) this.setValue(states.get(this._key) >= 99 ? 0 : 100, true);
      }, { signal });
      this.bindAutoReveal(signal);
      const show = () => {
        if (this._image.naturalWidth > 0) {
          this.dataset.ready = 'true';
          this.scheduleAutoReveal();
        }
      };
      this._image.addEventListener('load', show, { signal });
      this._image.addEventListener('error', () => this.removeAttribute('data-ready'), { signal });
      if (this._image.complete) show();
      this.render(states.get(this._key));
    }
    bindAutoReveal(signal) {
      this._isVisible = false;
      const visibilityChanged = entries => {
        const entry = entries[entries.length - 1];
        this._isVisible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= .55);
        this.scheduleAutoReveal();
      };
      if ('IntersectionObserver' in window) {
        this._visibilityObserver = new IntersectionObserver(visibilityChanged, { threshold: [0, .55] });
        this._visibilityObserver.observe(this);
      } else {
        const check = () => {
          const rect = this.getBoundingClientRect();
          this._isVisible = rect.width > 0 && rect.height > 0 && rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
          this.scheduleAutoReveal();
        };
        window.addEventListener('scroll', check, { signal, passive: true });
        window.addEventListener('resize', check, { signal, passive: true });
        check();
      }
      this._activeSlideObserver = new MutationObserver(() => this.scheduleAutoReveal());
      const slide = this.closest('.product-main-slide');
      const gallery = this.closest('.product-slideshow');
      if (slide) this._activeSlideObserver.observe(slide, { attributes: true, attributeFilter: ['class','aria-hidden'] });
      if (gallery) this._activeSlideObserver.observe(gallery, { attributes: true, attributeFilter: ['class'] });
      this._activeSlideObserver.observe(this, { attributes: true, attributeFilter: ['data-zoom-active'] });
      document.addEventListener('visibilitychange', () => this.scheduleAutoReveal(), { signal });
    }
    isActivePhoto() {
      if (this.dataset.zoom === 'true') return this.dataset.zoomActive === 'true';
      const slide = this.closest('.product-main-slide');
      if (!slide) return true;
      if (slide.getAttribute('aria-hidden') === 'true') return false;
      const gallery = slide.closest('.product-slideshow');
      if (gallery?.classList.contains('flickity-enabled')) return slide.classList.contains('is-selected');
      if (gallery && gallery.querySelectorAll('.product-main-slide').length > 1) return slide.classList.contains('starting-slide') || slide.classList.contains('is-selected');
      return true;
    }
    scheduleAutoReveal() {
      clearTimeout(this._autoTimer);
      const eligible = () => this._active && this.isConnected && this.dataset.ready === 'true' && this._isVisible && this.isActivePhoto() && !document.hidden && !revealedByCustomer.has(this._key);
      if (!eligible()) return;
      // Wait for the gallery/zoom opening movement to settle, then demonstrate
      // the slider once. No extra click on the image or controls is required.
      this._autoTimer = setTimeout(() => { if (eligible()) this.revealAutomatically(); }, 250);
    }
    revealAutomatically() {
      if (revealedByCustomer.has(this._key)) return;
      revealedByCustomer.add(this._key);
      cancelAnimation(this._key);
      const from = states.get(this._key) || 0;
      if (from >= 100 || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        publish(this._key, 100);
        return;
      }
      const animation = { frame: 0, owner: this, start: null };
      animations.set(this._key, animation);
      const step = timestamp => {
        if (animations.get(this._key) !== animation) return;
        if (animation.start === null) animation.start = timestamp;
        const progress = Math.min(1, (timestamp - animation.start) / 1000);
        const eased = progress * progress * (3 - 2 * progress);
        publish(this._key, from + (100 - from) * eased);
        if (progress < 1) animation.frame = requestAnimationFrame(step);
        else animations.delete(this._key);
      };
      animation.frame = requestAnimationFrame(step);
    }
    setValue(value, animate = false) {
      cancelAnimation(this._key);
      revealedByCustomer.add(this._key);
      publish(this._key, clamp(value), animate);
    }
    render(value) {
      this.style.setProperty('--fb-rubbel-reveal', value + '%');
      this._range.value = String(value);
      this._range.setAttribute('aria-valuetext', value === 0 ? 'Nachricht verdeckt' : value === 100 ? 'Nachricht vollständig sichtbar' : 'Nachricht zu ' + Math.round(value) + ' Prozent sichtbar');
      const button = this.querySelector('.fb-rubbel__toggle');
      if (button) {
        button.setAttribute('aria-pressed', String(value >= 99));
        button.querySelector('[data-fb-rubbel-label]').textContent = value >= 99 ? 'Wieder verdecken' : 'Nachricht entdecken';
      }
    }
    disconnectedCallback() {
      cancelAnimationFrame(this._frame);
      if (animations.get(this._key)?.owner === this) cancelAnimation(this._key);
      this._abort?.abort();
      clearTimeout(this._autoTimer);
      this._visibilityObserver?.disconnect();
      this._activeSlideObserver?.disconnect();
      subscribers.get(this._key)?.delete(this);
      if (subscribers.get(this._key)?.size === 0) subscribers.delete(this._key);
      this._active = false;
    }
  }
  if (!customElements.get('fb-rubbel-compare')) customElements.define('fb-rubbel-compare', RubbelCompare);

  // PhotoSwipe 4 integration: each overlay lives in the same transformed zoom
  // container as its photo and uses that photo's current, untransformed size.
  function attachZoom(gallery) {
    if (!gallery || gallery._fbRubbelAttached) return;
    gallery._fbRubbelAttached = true;
    const entries = new Map();
    let frame;
    const sync = () => {
      frame = 0;
      for (const item of gallery.items || []) {
        const container = item.container;
        const original = item.el?.closest('.image-wrap')?.querySelector('fb-rubbel-compare');
        if (!container || !original || item.loadError) continue;
        const image = container.querySelector('img.pswp__img:not(.pswp__img--placeholder)') || container.querySelector('img.pswp__img');
        if (!image) continue;
        let entry = entries.get(container);
        if (!entry) {
          const overlay = original.cloneNode(true);
          overlay.dataset.zoom = 'true';
          overlay.dataset.zoomActive = String(gallery.currItem === item);
          overlay.removeAttribute('data-ready');
          container.appendChild(overlay);
          entry = { overlay, image, observer: null, resize: null };
          entry.resize = () => {
            const current = entry.image;
            overlay.style.width = (current.offsetWidth || parseFloat(current.style.width) || item.w) + 'px';
            overlay.style.height = (current.offsetHeight || parseFloat(current.style.height) || item.h) + 'px';
            overlay.style.right = 'auto';
            overlay.style.bottom = 'auto';
          };
          entry.observer = new ResizeObserver(entry.resize);
          entry.observer.observe(image);
          entries.set(container, entry);
        } else if (entry.image !== image) {
          entry.observer.unobserve(entry.image);
          entry.image = image;
          entry.observer.observe(image);
        }
        entry.overlay.dataset.zoomActive = String(gallery.currItem === item);
        entry.resize();
      }
      for (const [container, entry] of entries) {
        if (!container.isConnected) { entry.observer.disconnect(); entry.overlay.remove(); entries.delete(container); }
      }
    };
    const schedule = () => { if (!frame) frame = requestAnimationFrame(sync); };
    for (const name of ['afterChange','imageLoadComplete','resize','initialZoomInEnd']) gallery.listen(name, schedule);
    gallery.listen('destroy', () => {
      cancelAnimationFrame(frame);
      entries.forEach(entry => { entry.observer.disconnect(); entry.overlay.remove(); });
      entries.clear();
    });
    schedule();
  }
  window.FreudeboxRubbel = { attachZoom };
})();
